// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { useMutationWithGlobalErrorHandler } from '@/hooks/use-mutation-with-global-error-handler';
import { Subscriptions } from '@/notification/api/notification-types';
import {
  SetEntityWatchInput,
  SubscriptionInput,
  getMessageTypes,
  getSubscriptions,
  replaceSubscriptions,
  setEntityWatch,
} from '@/notification/api/subscription';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const SUBSCRIPTIONS_QUERY_KEY = 'notification-subscriptions';
const MESSAGE_TYPES_QUERY_KEY = 'notification-message-types';

/** Reads what the caller can subscribe to, which no user action changes. */
export function useMessageTypes() {
  return useQuery({
    queryKey: [MESSAGE_TYPES_QUERY_KEY],
    queryFn: getMessageTypes,
    staleTime: Infinity,
  });
}

/** Reads the caller's type subscriptions and watched records. */
export function useNotificationSubscriptions() {
  return useQuery({
    queryKey: [SUBSCRIPTIONS_QUERY_KEY],
    queryFn: getSubscriptions,
  });
}

/**
 * Saves what the caller wants to hear about. Both routes answer with the whole
 * of the caller's interests, so the read is seeded rather than refetched.
 */
export function useSaveNotificationSubscriptions() {
  const queryClient = useQueryClient();

  const saveSubscriptions = useMutationWithGlobalErrorHandler({
    mutationFn: (input: {
      offeredMessageTypes: string[];
      subscriptions: SubscriptionInput[];
    }) => replaceSubscriptions(input.offeredMessageTypes, input.subscriptions),
    // The card renders its own alert beside the Save button, so the global
    // support banner would say the same sentence a second time
    disableGlobalError: true,
    onSuccess: (data) => {
      queryClient.setQueryData([SUBSCRIPTIONS_QUERY_KEY], data);
    },
  });

  // One record at a time, because each is its own route. Sending them at once
  // would leave whichever answer landed last deciding the cache, so a slow
  // reply could put a record the caller turned off back on the screen
  const saveEntityWatches = useMutationWithGlobalErrorHandler({
    mutationFn: async (inputs: SetEntityWatchInput[]) => {
      let latest: Subscriptions | undefined;
      for (const input of inputs) {
        latest = await setEntityWatch(input);
      }
      return latest;
    },
    disableGlobalError: true,
    onSuccess: (data) => {
      if (data !== undefined) {
        queryClient.setQueryData([SUBSCRIPTIONS_QUERY_KEY], data);
      }
    },
    // The loop stops where it failed, so the records it already got through are
    // off on the server while the screen still lists them. Re-read rather than
    // leave the caller looking at a list that is no longer true
    onError: async () => {
      await queryClient.invalidateQueries({
        queryKey: [SUBSCRIPTIONS_QUERY_KEY],
      });
    },
  });

  return { saveSubscriptions, saveEntityWatches };
}
